import React, { useEffect, useState } from 'react'
import { css } from '@emotion/react'
import { colors, Button, Tooltip } from 'plume-ui'
import { IcomoonIcon } from '@/components/atoms/IcomoonIcon'
import { Modal, ModalCloseButton, ModalTitle } from '@/components/atoms/Modal'
import { SourcingAnnotationCandidate } from '../reducers/sourcingAnnotationCandidates'
import { SourcingAnnotationCandidateDeleteConfirmModal } from './SourcingAnnotationCandidateDeleteConfirmModal'

type Candidate = SourcingAnnotationCandidate

type Props = {
  isHidden: boolean
  modalId: string
  modalName: string
  children: Candidate[]
  onCreate: (name: string) => void
  onUpdate: (id: number, name: string) => void
  onDelete: (id: number) => void
  onClose: () => void
}

const ENTER_CODE = 'Enter'

const FOCUS_STYLE = css`
  &:focus-visible {
    outline: 2px solid ${colors.black10};
    outline-offset: 0.15rem;
    transition: initial;
    border: 1px solid ${colors.gray80};
  }
`

export const SourcingAnnotationCandidateEditModal = (props: Props) => {
  const [newText, setNewText] = useState('')
  const [hasEmptyError, setHasEmptyError] = useState(false)
  const [deleteCandidate, setDeleteCandidate] = useState<Candidate | null>(null)
  const [isComposing, setIsComposing] = useState(false)

  const handleChange = (text: string) => {
    setNewText(text)
  }
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.code === ENTER_CODE) {
      e.preventDefault()
      // IME入力中はEnterを無視する
      if (isComposing) return
      handleAdd()
    }
  }

  const handleAdd = () => {
    addCandidateWithClear(newText)
  }

  const handleDeleteRequest = (candidate: Candidate) => {
    setDeleteCandidate(candidate)
  }

  const handleDelete = (candidateId: number) => {
    props.onDelete && props.onDelete(candidateId)
    setDeleteCandidate(null)
  }

  const handleCancel = () => {
    setDeleteCandidate(null)
  }

  const addCandidateWithClear = (CandidateName: string) => {
    if (validateInput(CandidateName)) {
      props.onCreate(CandidateName)
      setNewText('')
      setHasEmptyError(false)
    } else {
      setHasEmptyError(true)
    }
  }

  const validateInput = (route: string) => {
    return route.length > 0
  }

  return (
    <>
      <Modal isHidden={props.isHidden} zIndex={104} onClose={props.onClose}>
        <div
          css={css`
            position: fixed;
            left: 50%;
            top: 50%;
            z-index: 102;
            transform: translate(-50%, -50%);
            box-sizing: border-box;
            width: 586px;
            min-height: 146px;
            background: ${colors.white00};
            vertical-align: middle;
            padding: 28px 32px 32px;
            border-radius: 8px;
          `}
          data-test-id={`${props.modalId}-edit-modal`}
        >
          <div
            css={css`
              display: flex;
              flex-direction: row;
              justify-content: space-between;
              align-items: flex-end;
              margin-bottom: 24px;
            `}
          >
            <ModalTitle data-test-id="title">
              {`${props.modalName}項目を編集`}
            </ModalTitle>
            <ModalCloseButton data-test-id="close" onClick={props.onClose} />
          </div>
          <ul
            data-test-id={props.modalId}
            css={css`
              text-align: left;
              padding: 0;
              margin: 0 0 8px;
              font-size: 14px;
              max-height: calc(100vh - 124px - 96px);
              overflow-y: auto;
            `}
          >
            {props.children.length > 0 ? (
              props.children.map((route) => (
                <EditableListItem
                  key={route.id}
                  modalId={props.modalId}
                  modalName={props.modalName}
                  onUpdate={props.onUpdate}
                  onDeleteRequest={handleDeleteRequest}
                >
                  {route}
                </EditableListItem>
              ))
            ) : (
              <li
                css={css`
                  color: ${colors.textLight};
                `}
              >
                {`${props.modalName}項目はまだありません`}
              </li>
            )}
          </ul>
          <div
            css={css`
              display: flex;
              gap: 8px;
            `}
          >
            <input
              type="text"
              data-test-id="new-option"
              value={newText}
              placeholder={`${props.modalName}名を入力`}
              onChange={(e) => handleChange(e.target.value)}
              onCompositionStart={() => setIsComposing(true)}
              onCompositionEnd={() => setIsComposing(false)}
              onKeyDown={handleKeyDown}
              css={css`
                flex: 1;
                border: 1px solid ${colors.gray60};
                border-radius: 2px;
                font-size: 14px;
                padding: 0 8px 2px;
                line-height: 28px;

                &::placeholder {
                  color: ${colors.textLight};
                  font-size: 13px;
                }

                ${FOCUS_STYLE}
              `}
            />
            <Button
              size="small"
              data-test-id="add-option"
              css={FOCUS_STYLE}
              onClick={() => handleAdd()}
            >
              追加
            </Button>
          </div>
          {hasEmptyError && (
            <div
              data-test-id="add-error"
              css={css`
                padding: 4px 0 0 4px;
                color: ${colors.red60};
                font-size: 12px;
              `}
            >
              {`${props.modalName}名を入力してください`}
            </div>
          )}
        </div>
      </Modal>
      <SourcingAnnotationCandidateDeleteConfirmModal
        isHidden={!deleteCandidate}
        modalId={props.modalId}
        modalName={props.modalName}
        candidate={deleteCandidate}
        setDelete={setDeleteCandidate}
        onDelete={handleDelete}
        onCancel={handleCancel}
      />
    </>
  )
}

const ESC_CODE = 'Escape'
const COMMIT_TIMEOUT = 1500

type EditableListItemProps = {
  modalId: string
  modalName: string
  children: Candidate
  onUpdate: (id: number, name: string) => void
  onDeleteRequest: (candidate: Candidate) => void
}

export const EditableListItem = (props: EditableListItemProps) => {
  const [editing, setEditing] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [committing, setCommitting] = useState(false)
  const [committingValue, setCommittingValue] = useState('')
  const [isComposing, setIsComposing] = useState(false)

  useEffect(() => {
    clearCommittingState()
  }, [props.children])

  const beginEditing = () => {
    setInputValue(props.children.name)
    setEditing(true)
  }

  const commitEditing = () => {
    props.onUpdate(props.children.id, inputValue)
    setEditing(false)

    // 候補名の変更をリクエストしてから、実際に反映されるまでラグがある。
    // それを解消するために、変更後のテキストを保持しておいて、リクエストが返ってくるまではそれを表示する。
    // もしリクエストが失敗したりタイムアウトした場合は元に戻る。
    setTimeout(clearCommittingState, COMMIT_TIMEOUT)
    setCommitting(true)
    setCommittingValue(inputValue)
  }

  const rollbackEditing = () => {
    setEditing(false)
  }

  const clearCommittingState = () => {
    setCommitting(false)
    setCommittingValue('')
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.code) {
      case ENTER_CODE:
        e.preventDefault()
        // IME入力中はEnterを無視する
        if (isComposing) return
        commitEditing()
        break
      case ESC_CODE:
        rollbackEditing()
        break
    }
  }

  const handleBlur = (_e: React.FocusEvent<HTMLInputElement>) => {
    if (inputValue) {
      commitEditing()
    } else {
      rollbackEditing()
    }
  }

  const handleDelete = (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    candidate: Candidate
  ) => {
    e.stopPropagation()
    props.onDeleteRequest && props.onDeleteRequest(candidate)
  }

  return editing ? (
    <li
      data-test-id={`${props.modalId}-item`}
      css={css`
        height: 37px;
        border: 1px solid #0071e3;
      `}
    >
      <input
        type="text"
        value={inputValue}
        autoFocus
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={`${props.modalName}名`}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => setIsComposing(false)}
        css={css`
          color: ${colors.text};
          font-size: 14px;
          letter-spacing: 0.018em;
          width: 100%;
          height: 100%;
          outline: none;
          border: 0;
          padding-left: 7px;
          padding-bottom: 6px;
        `}
        data-test-id={`${props.modalId}-edit-name-input`}
      />
    </li>
  ) : (
    <li
      data-test-id={`${props.modalId}-item`}
      css={css`
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        height: 37px;
        padding: 8px;
        border-bottom: 1px solid ${colors.border};
        cursor: text;
        button {
          opacity: 0;
        }
        transition: background-color 0.3s ease;
        &:hover {
          border-color: ${colors.border};
          background-color: ${colors.backgroundGray};
          button {
            opacity: 1;
          }
        }
      `}
      onClick={() => beginEditing()}
    >
      <span
        css={css`
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          line-height: 1.5;
        `}
      >
        {committing ? committingValue : props.children.name}
      </span>
      <Tooltip label="削除">
        <button
          data-test-id={`delete-${props.modalId}`}
          css={css`
            display: flex;
            align-items: center;
            border: 0;
            border-radius: 50%;
            width: 32px;
            height: 32px;
            transition: background-color 0.3s ease;
            transition: opacity 0.3s ease;
            cursor: pointer;
            &:hover {
              background-color: ${colors.backgroundGray};
            }
          `}
          onClick={(e) => {
            handleDelete(e, props.children)
          }}
        >
          <IcomoonIcon
            name="trash-fill"
            css={css`
              font-size: 20px;
              border-radius: 18px;
              color: ${colors.textLight};
            `}
          />
        </button>
      </Tooltip>
    </li>
  )
}
